import axios from "axios";
import useSWR from "swr";
import closestTo from "date-fns/closestTo";
import format from "date-fns/format";
import add from "date-fns/add";
import isToday from "date-fns/isToday";
import qs from "qs";
import apiClient from "@/utils/apiClient";

/**
 * Simple object check.
 * @param item
 * @returns {boolean}
 */
export function isObject(item) {
  return item && typeof item === "object" && !Array.isArray(item);
}

export const useVenueNames = (id) => {
  const fetcher = (url) => axios.get(url).then((res) => res.data);
  const { data, error, mutate } = useSWR(
    `https://www.ambulante.org/wp-json/wp/v2/sedes?slug=${id}`,
    fetcher
  );

  return {
    data: data && data[0] ? data[0].name : "",
    isLoading: !error && !data,
    error,
    mutate,
  };
};

export const useProjections = (filters = {}) => {
  let query = qs.stringify({
    populate: {
      peliculas: {
        populate: {
          film_cover_image: "*",
          Directores: "*",
          film_year: "*",
          countries: "*",
        },
      },
      live_screenings: "*",
      online_screenings: "*",
      tv_screenings: "*",
      sede: "*",
    },
    sort: "tv_screenings.date:asc",
    pagination: {
      pageSize: 200,
    },
    filters: {
      ...filters,
    },
  });

  const fetcher = (url) => apiClient.get(url).then((res) => res.data);

  const { data, error, mutate } = useSWR(`/api/screenings?${query}`, fetcher);
  return { data, isLoading: !error && !data, error, mutate };
};

export const getEvents = (filters = {}) => {
  const now = new Date();
  let query = qs.stringify({
    populate: {
      films: {
        populate: {
          film_year: "*",
          Directores: "*",
        },
      },
      peliculas: "*",
      sede: "*",
      event_image: "*",
    },
    pagination: {
      pageSize: 200,
    },
    filters: {
      ...filters,
      $and: [
        {
          date_time: {
            $gte: now,
          },
        },
      ],
    },
  });


  return apiClient.get(`/api/events?${query}`);
};

export const useProjectionsDates = () => {
  const endpoints = [
    "https://ambulante.org/wp-json/ambulante_api/v1/onsite_events",
    "https://ambulante.org/wp-json/ambulante_api/v1/events",
  ];
  const fetcher = (k) =>
    axios.all(endpoints.map((l) => axios.get(l))).then((r) => r);

  const { data: events, error, mutate } = useSWR("event_endpoints", fetcher);

  const data = events?.length > 0 ? events[0].data.concat(events[1].data) : [];

  const byCityDate = data.length > 0 ? groupVenuesByCityDate(data) : [];

  const closestDay = isObject(byCityDate)
    ? getClosestDate(byCityDate)
    : "31-8-2020";

  return {
    data: byCityDate,
    isLoading: !error && !data,
    error,
    mutate,
    closestDay,
  };
};

function getClosestDate(venues) {
  if (venues) {
    let dates = [];
    Object.keys(venues).forEach((k) => {
      if (!k.includes("rep")) {
        dates.push(...venues[k]);
      }
    });
    const datesArray = dates.map((d) => {
      const s = d.split("-");
      return new Date(s[2], s[1], s[0]);
    });

    const f = !!datesArray.find((d) => isToday(d));
    if (f) {
      const closest = new Date();
      const g =
        closest.getDate() +
        "-" +
        closest.getMonth() +
        "-" +
        closest.getFullYear();
      return g;
    }

    // const closest = closestTo(new Date(), datesArray);

    const g = "";
    // const g =
    //   closest.getDate() +
    //   "-" +
    //   closest.getMonth() +
    //   "-" +
    //   closest.getFullYear();

    return g;
  }
}

const groupEventsByStartDay = (events = []) => {
  return events.reduce((acc, event) => {
    const formatForDate = event.start_date.split(" ")[0].split("-");

    const startDay = new Date(
      formatForDate[0],
      formatForDate[1] - 1,
      formatForDate[2]
    );
    const formatted = `${startDay.getDate()}-${startDay.getMonth()}-${startDay.getFullYear()}`;

    if (!acc[formatted]) {
      acc[formatted] = [];
    }

    acc[formatted].push(event);
    return acc;
  }, {});
};

const groupEventsByPlaceName = (events = []) => {
  return events.reduce((acc, event) => {
    const placeName = event.place_name;

    if (!acc[placeName]) {
      acc[placeName] = [];
    }

    acc[placeName].push(event);
    return acc;
  }, {});
};

const groupVenuesByCityDate = (venues) => {
  const cities = {};

  venues.forEach((v) => {
    const formatForDate = v.start_date.split(" ")[0].split("-");

    const event_date = new Date(
      formatForDate[0],
      formatForDate[1] - 1,
      formatForDate[2]
    );

    const past = dateInPast(event_date, new Date());
    const formatted = `${event_date.getDate()}-${event_date.getMonth()}-${event_date.getFullYear()}`;

    if (!past) {
      if (!cities[v.city_name]) {
        cities[v.city_name] = [];
      }
      if (!cities[v.city_name].includes(formatted)) {
        cities[v.city_name].push(formatted);
      }
    }
  });
  return cities;
};

const dateInPast = function (firstDate, secondDate) {
  if (firstDate.setHours(0, 0, 0, 0) < secondDate.setHours(0, 0, 0, 0)) {
    return true;
  }

  return false;
};

export const getTodayEvents = async () => {
  const todayStart = format(new Date(), "yyy-MM-dd");
  const tomorrow = add(new Date(), { days: 1 });
  const todayEnd = format(new Date(tomorrow).setHours(0), "yyy-MM-dd");

  let query = qs.stringify({
    populate: "*",
    sort: ["date_time:ASC"],
    pagination: {
      pageSize: 50,
    },
    filters: {
      $and: [
        {
          date_time: {
            $gt: todayStart + "T06:00:00.000Z",
          },
        },
        {
          date_time: {
            $lt: todayEnd + "T06:00:00.000Z",
          },
        },
      ],
    },
  });

  return apiClient.get(`/api/events?${query}`);
};

export const getTodayProjections = async (filters = {}) => {
  const now = new Date();
  const todayStart = format(new Date(), "yyy-MM-dd");
  const tomorrow = add(new Date(), { days: 2 });
  const todayEnd = format(new Date(tomorrow).setHours(0), "yyy-MM-dd");

  let query = qs.stringify({
    populate: {
      peliculas: {
        populate: {
          film_cover_image: "*",
          filmSlug: "*",
        },
      },
      live_screenings: "*",
      tv_screenings: "*",
      online_screenings: "*",
      sede: "*",
    },
    sort: ["date_time:asc"],
    pagination: {
      pageSize: 50,
    },

    filters: {
      $or: [
        {
          tv_screenings: {
            end_date: {
              $lt: todayEnd,
            },
            date: {
              $gte: now,
            },
          },
        },

        {
          live_screenings: {
            date: {
              $lt: todayEnd,
              $gte: now,
            },
          },
        },

        {
          online_screenings: {
            start_date: {
              $lt: todayEnd,
            },
            end_date: {
              $gte: now,
            },
          },
        },
      ],
    },
  });

  return apiClient.get(`/api/screenings?${query}`);
};

export const getEventsByMonths = async (month, filters = {}) => {
  // get closest year based on month
  const closestYear = new Date().getFullYear();
  const firstDay = new Date(closestYear, month, 1);
  const lastDay = new Date(closestYear, month + 1, 1);
  const today = new Date();
  const isCurrentMonth = today.getMonth() === month;

  let query = qs.stringify({
    populate: "*",
    sort: ["date_time:asc"],
    pagination: {
      pageSize: 50,
    },
    filters: {
      $and: [
        {
          date_time: {
            $gte: isCurrentMonth ? today : format(firstDay, "yyy-MM-dd"),
          },
        },
        {
          date_time: {
            $lt: format(lastDay, "yyy-MM-dd"),
          },
        },
      ],
      $or: [...filters?.$or],
    },
  });

  return apiClient.get(`/api/events?${query}`);
};

export const getProyectionsByMonths = async (
  month,
  filters = {},
  screeningsType
) => {
  const closestYear = new Date().getFullYear();
  const firstDay = new Date(closestYear, month, 1);
  const lastDay = new Date(closestYear, month + 1, 1);
  const today = new Date();
  const isCurrentMonth = today.getMonth() === month;
  const screeningsFilters = {
    tv: [
      {
        tv_screenings: {
          end_date: {
            $lt: format(lastDay, "yyy-MM-dd"),
          },
        },
      },
      {
        tv_screenings: {
          date: {
            $gte: format(isCurrentMonth ? today : firstDay, "yyy-MM-dd"),
          },
        },
      },
    ],
    live: [
      {
        live_screenings: {
          date: {
            $lt: format(lastDay, "yyy-MM-dd"),
          },
        },
      },
      {
        live_screenings: {
          date: {
            $gte: format(isCurrentMonth ? today : firstDay, "yyy-MM-dd"),
          },
        },
      },
    ],
    online: [
      {
        online_screenings: {
          start_date: {
            $lt: format(lastDay, "yyy-MM-dd"),
          },
        },
      },
      {
        online_screenings: {
          end_date: {
            $gte: format(isCurrentMonth ? today : firstDay, "yyy-MM-dd"),
          },
        },
      },
    ],
  };

  let query = qs.stringify({
    populate: {
      peliculas: {
        populate: {
          film_cover_image: "*",
          filmSlug: "*",
        },
      },
      live_screenings: "*",
      tv_screenings: "*",
      online_screenings: "*",
      sede: "*",
    },
    sort: ["date_time:asc"],
    pagination: {
      pageSize: 50,
    },

    filters: {
      $and: screeningsFilters[screeningsType],
      $or: [...filters?.$or],
    },
  });

  return apiClient.get(`/api/screenings?${query}`);
};
